import React, {lazy, Suspense, useCallback, useEffect, useMemo, useState} from 'react';
import {Outlet, useLocation, useNavigate} from "react-router-dom";

import LayoutHeader from "./LayoutHeader";
import SideBar from "./SideBar";
import TopMenu from "./TopMenu";
import {getMenuJson, getTopMenuIdByPathname} from "../components/commonmethod/CommonMethod";
import {useSelector} from "react-redux";

const LayoutExcelGridOverlay = lazy(() => import("../components/js-grid/LayoutExcelGridOverlay"));

function LayoutContainer() {
    const menu = getMenuJson();
    const location = useLocation();
    const navigate = useNavigate();

    const routeTopMenuId = useMemo(
        () => getTopMenuIdByPathname(location.pathname, menu) ?? "",
        [location.pathname, menu]
    );

    const firstTopMenuId = useMemo(
        () => (menu.length ? String(menu[0].id) : ""),
        [menu],
    );

    const resolvedTopMenuId = routeTopMenuId || firstTopMenuId;

    const [openedTopMenuId, setOpenedTopMenuId] = useState("");
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const isLoggedIn = useSelector((s) => s.auth.isLoggedIn);

    useEffect(() => {
        const returnTo = sessionStorage.getItem("returnTo");

        if (returnTo && isLoggedIn) {
            sessionStorage.removeItem("returnTo");
            navigate(returnTo, { replace: true });
        }
    }, [isLoggedIn, navigate]);

    useEffect(() => {
        if (!isLoggedIn || !firstTopMenuId) return;
        if (sessionStorage.getItem("initTopMenu") !== "1") return;

        sessionStorage.removeItem("initTopMenu");
        setOpenedTopMenuId(firstTopMenuId);
        setSidebarOpen(true);
        localStorage.setItem("topMenuId", firstTopMenuId);
    }, [isLoggedIn, firstTopMenuId]);

    useEffect(() => {
        if (resolvedTopMenuId) {
            localStorage.setItem("topMenuId", resolvedTopMenuId);
        }
    }, [resolvedTopMenuId]);

    useEffect(() => {
        document.body.classList.toggle('sidebar-drawer-open', sidebarOpen);
        return () => document.body.classList.remove('sidebar-drawer-open');
    }, [sidebarOpen]);

    const handleTopMenuClick = useCallback((id) => {
        const idStr = String(id);
        if (sidebarOpen && openedTopMenuId === idStr) {
            setSidebarOpen(false);
            return;
        }
        setOpenedTopMenuId(idStr);
        setSidebarOpen(true);
        localStorage.setItem("topMenuId", idStr);
    }, [sidebarOpen, openedTopMenuId]);

    const handleCloseSidebar = useCallback(() => {
        setSidebarOpen(false);
    }, []);

    // 닫힐 때 routeTopMenuId로 바꾸면 슬라이드 아웃 중 하위메뉴가 깜빡임 → 마지막으로 연 탑메뉴 유지
    const sidebarTopMenuId = openedTopMenuId || resolvedTopMenuId;
    const activeTopMenu = useMemo(
        () => menu.find((m) => String(m.id) === String(sidebarTopMenuId)) ?? menu[0],
        [menu, sidebarTopMenuId]
    );

    const headerMenus = useMemo(() => {
        const matched = menu.filter((m) => String(m.id) === resolvedTopMenuId);
        return matched.length ? matched : menu.length ? [menu[0]] : [];
    }, [menu, resolvedTopMenuId]);

    return (
        <div
            className={`app-layout flex h-screen max-h-screen min-h-0 overflow-hidden ${sidebarOpen ? "sidebar-drawer-open" : "sidebar-drawer-closed"}`}
        >
            <TopMenu
                menus={menu}
                routeTopMenuId={resolvedTopMenuId}
                openedTopMenuId={sidebarOpen ? openedTopMenuId : ""}
                onMenuClick={handleTopMenuClick}
                onCloseDrawer={handleCloseSidebar}
            />
            <div className="layout-shell flex flex-1 min-h-0 min-w-0 relative">
                <aside
                    className={`layout-sidebar-drawer ${sidebarOpen ? "is-open bg-lightGray" : ""}`}
                    aria-hidden={!sidebarOpen}
                >
                    <SideBar
                        data={activeTopMenu?.children ?? []}
                        topName={activeTopMenu?.name}
                        topIcon={activeTopMenu?.icon}
                    />
                </aside>
                <div className="layout-container flex flex-1 flex-col min-h-0 overflow-hidden">
                    <LayoutHeader
                        menus={headerMenus}
                        totalMenu={menu}
                        clicked={resolvedTopMenuId}
                        setClicked={() => {}}
                    />
                    <main className="layout-main">
                        <Outlet />
                    </main>
                </div>
            </div>
            <Suspense fallback={null}>
                <LayoutExcelGridOverlay />
            </Suspense>
        </div>
    );
}

export default LayoutContainer;